import React from 'react';
import { Calculator, Gift, ArrowRight, CheckCircle, PieChart, TrendingUp, Target } from 'lucide-react';

const LeadMagnet = () => {
  const benefits = [
    "7年間の実績データに基づく精度の高いシミュレーション",
    "初期投資・月間収支・投資回収期間を一目で確認",
    "稼働率別の感度分析で戦略立案をサポート",
    "パートナーシップの結果に関わらず無料でご利用可能"
  ];

  return (
    <section className="py-20 lg:py-28 bg-gradient-to-b from-white to-amber-50/30">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section badge */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full">
            <Gift className="w-4 h-4 text-amber-700" />
            <span className="text-sm text-amber-800 font-medium tracking-wide">
              無料特典
            </span>
          </div>
        </div>

        {/* Section header */}
        <div className="text-center mb-12">
          <p className="text-amber-800/80 text-base tracking-[0.2em] uppercase mb-4">
            Free Financial Simulation Tool
          </p>
          <h2 className="text-4xl md:text-5xl font-light text-stone-800 mb-6">
            収支シミュレーションアプリ
          </h2>
          <div className="w-20 h-px bg-amber-700/40 mx-auto mb-8" />
          <p className="text-lg text-stone-600 font-light leading-relaxed">
            パートナーシップ適合性チェックにお申し込みいただくと、<br className="hidden md:block" />
            <span className="text-stone-800 font-medium">収支シミュレーションアプリへの無料アクセス</span>をご提供いたします。
          </p>
        </div>

        {/* Content grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* App preview - Mock Dashboard */}
          <div className="relative group">
            <div className="absolute -inset-4 bg-gradient-to-r from-amber-200/20 to-stone-200/20 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-500" />
            <div className="relative bg-white rounded-2xl shadow-xl overflow-hidden border border-stone-200">
              {/* Browser-like header */}
              <div className="bg-stone-100 px-4 py-3 flex items-center gap-2 border-b border-stone-200">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-amber-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                </div>
                <div className="flex-1 flex justify-center">
                  <div className="bg-white px-4 py-1 rounded text-xs text-stone-500">
                    ACOE 収支シミュレーション
                  </div>
                </div>
              </div>
              
              {/* Mock Dashboard Content */}
              <div className="p-6 bg-gradient-to-br from-stone-50 to-amber-50/30">
                {/* Dashboard Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h4 className="text-stone-800 font-medium">収支シミュレーション結果</h4>
                    <p className="text-xs text-stone-500">新宿2丁目</p>
                  </div>
                  <div className="text-xs text-amber-700 bg-amber-100 px-2 py-1 rounded">
                    シミュレーション完了
                  </div>
                </div>

                {/* Key Metrics Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                  <div className="bg-white rounded-lg p-4 shadow-sm border border-stone-100">
                    <p className="text-xs text-stone-500 mb-1">初期投資合計</p>
                    <p className="text-lg font-semibold text-stone-800">¥17,510,000</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm border border-stone-100">
                    <p className="text-xs text-stone-500 mb-1">月間想定売上</p>
                    <p className="text-lg font-semibold text-stone-800">¥2,295,000</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm border border-stone-100">
                    <p className="text-xs text-stone-500 mb-1">月間営業利益</p>
                    <p className="text-lg font-semibold text-amber-700">¥1,003,050</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm border border-stone-100">
                    <p className="text-xs text-stone-500 mb-1">投資回収期間</p>
                    <p className="text-lg font-semibold text-stone-800">17.5<span className="text-sm font-normal">ヶ月</span></p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm border border-stone-100">
                    <p className="text-xs text-stone-500 mb-1">費用率</p>
                    <p className="text-lg font-semibold text-stone-800">56.3%</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm border border-stone-100">
                    <p className="text-xs text-stone-500 mb-1">利益率</p>
                    <p className="text-lg font-semibold text-green-600">43.7%</p>
                  </div>
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  {/* Pie Chart Placeholder 1 */}
                  <div className="bg-white rounded-lg p-4 shadow-sm border border-stone-100">
                    <p className="text-xs text-stone-600 mb-3">初期投資の内訳</p>
                    <div className="flex items-center justify-center">
                      <div className="relative w-20 h-20">
                        <svg viewBox="0 0 36 36" className="w-full h-full">
                          <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="#e7e5e4"
                            strokeWidth="3"
                          />
                          <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="#d97706"
                            strokeWidth="3"
                            strokeDasharray="40, 100"
                          />
                          <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="#78716c"
                            strokeWidth="3"
                            strokeDasharray="30, 100"
                            strokeDashoffset="-40"
                          />
                          <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="#a8a29e"
                            strokeWidth="3"
                            strokeDasharray="30, 100"
                            strokeDashoffset="-70"
                          />
                        </svg>
                        <PieChart className="absolute inset-0 m-auto w-6 h-6 text-stone-400" />
                      </div>
                    </div>
                  </div>

                  {/* Pie Chart Placeholder 2 */}
                  <div className="bg-white rounded-lg p-4 shadow-sm border border-stone-100">
                    <p className="text-xs text-stone-600 mb-3">月間費用の内訳</p>
                    <div className="flex items-center justify-center">
                      <div className="relative w-20 h-20">
                        <svg viewBox="0 0 36 36" className="w-full h-full">
                          <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="#e7e5e4"
                            strokeWidth="3"
                          />
                          <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="#059669"
                            strokeWidth="3"
                            strokeDasharray="35, 100"
                          />
                          <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="#0891b2"
                            strokeWidth="3"
                            strokeDasharray="25, 100"
                            strokeDashoffset="-35"
                          />
                          <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="#8b5cf6"
                            strokeWidth="3"
                            strokeDasharray="40, 100"
                            strokeDashoffset="-60"
                          />
                        </svg>
                        <PieChart className="absolute inset-0 m-auto w-6 h-6 text-stone-400" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sensitivity Analysis Preview */}
                <div className="bg-white rounded-lg p-4 shadow-sm border border-stone-100">
                  <p className="text-xs text-stone-600 mb-3">稼働率別 感度分析</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-stone-500 border-b border-stone-100">
                          <th className="text-left py-2 font-normal">稼働率</th>
                          <th className="text-right py-2 font-normal">月間利益</th>
                          <th className="text-right py-2 font-normal">回収期間</th>
                        </tr>
                      </thead>
                      <tbody className="text-stone-700">
                        <tr className="border-b border-stone-50">
                          <td className="py-1.5">60%</td>
                          <td className="text-right">¥603,050</td>
                          <td className="text-right">29.0ヶ月</td>
                        </tr>
                        <tr className="border-b border-stone-50 bg-amber-50/50">
                          <td className="py-1.5 font-medium">75%</td>
                          <td className="text-right font-medium text-amber-700">¥1,003,050</td>
                          <td className="text-right font-medium">17.5ヶ月</td>
                        </tr>
                        <tr>
                          <td className="py-1.5">90%</td>
                          <td className="text-right">¥1,403,050</td>
                          <td className="text-right">12.5ヶ月</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Floating badge */}
            <div className="absolute -bottom-4 -right-4 bg-stone-800 text-stone-50 px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
              <Calculator className="w-4 h-4" />
              <span className="text-sm font-medium">7年分の実績データ</span>
            </div>
          </div>

          {/* Benefits list */}
          <div className="lg:pl-8">
            <h3 className="text-3xl font-light text-stone-800 mb-6">
              物件の収益性を<br />
              <span className="text-amber-800">無料で診断</span>
            </h3>
            
            <p className="text-lg text-stone-600 font-light leading-relaxed mb-8">
              ACOEが7年間運営してきた実績データをもとに開発した収支シミュレーションアプリ。
              初期投資、月間収支、投資回収期間を詳細にシミュレーションできます。
            </p>

            <ul className="space-y-4 mb-10">
              {benefits.map((benefit, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-amber-700" />
                  </div>
                  <span className="text-base text-stone-600 font-light">{benefit}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <a
                href="#contact"
                className="inline-flex items-center gap-2 bg-stone-800 hover:bg-stone-700 text-stone-50 px-6 py-3 rounded-lg transition-all button-hover group"
              >
                <span>無料で利用する</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <span className="text-stone-500 text-sm">
                適合性チェック申込で<br className="sm:hidden" />すぐにアクセス可能
              </span>
            </div>
          </div>
        </div>

        {/* App features highlight */}
        <div className="mt-20 grid md:grid-cols-3 gap-6">
          {[
            {
              icon: Calculator,
              title: "収支シミュレーション",
              description: "初期投資・月間収支・利益率を自動計算",
              metric: "自動計算"
            },
            {
              icon: TrendingUp,
              title: "感度分析",
              description: "稼働率別の収益変動をシミュレーション",
              metric: "稼働率別"
            },
            {
              icon: Target,
              title: "目標設定",
              description: "損益分岐点と目標達成ADRを可視化",
              metric: "戦略立案"
            }
          ].map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div
                key={index}
                className="bg-white rounded-xl p-6 border border-stone-100 hover:border-amber-200 hover-lift card-gradient-accent"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-amber-700" />
                  </div>
                  <span className="text-amber-700 text-xs tracking-wider uppercase">
                    {feature.metric}
                  </span>
                </div>
                <h4 className="text-xl font-medium text-stone-800 mb-2">
                  {feature.title}
                </h4>
                <p className="text-stone-500 text-base font-light">
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default LeadMagnet;
